import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertTriangle, Crown, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import BottomNavigation from "@/components/BottomNavigation";
import useSubscription from "@/contexts/SubscriptionContext";
import { Capacitor } from "@capacitor/core";
import MobileStoreRedirect from "@/components/ui/MobileStoreRedirect";
import { useTranslation } from "react-i18next";
import { ScreenHeader, MonoValue } from "@/components/redesign";

const SubscriptionExpiredPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { status } = useSubscription();
  const { plans, planType, expiresAt } = status;
  const isMobilePlatform = Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';
  const { t } = useTranslation();

  // Get return path from location state if available
  const returnPath = location.state?.returnPath;

  // Determine if this was a trial or paid subscription that expired
  const wasTrial = planType === 'expired' && !expiresAt;
  const pageTitle = wasTrial ? t('subscription_expired.trial_expired_title') : t('subscription_expired.subscription_expired_title');

  // Format expiration date if available
  const formattedExpireDate = expiresAt ? new Date(expiresAt).toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : null;

  return (
    <div className="page-container bg-gray-50">
      <ScreenHeader
        title={pageTitle}
        showBack={true}
      />

      <main className="flex-1 px-4 py-4 overflow-y-auto pb-20 space-y-4">
        {/* Mobile store redirect message for PC users */}
        {!isMobilePlatform && (
          <div>
            <MobileStoreRedirect variant="detailed" />
          </div>
        )}

        {/* Expired notice */}
        <div className="bg-error-50 border border-error-100 rounded-2xl p-6 text-center">
          <div className="bg-error-100 rounded-full inline-flex items-center justify-center p-4 mb-4">
            <AlertTriangle className="text-error-600 h-8 w-8" />
          </div>
          <h2 className="text-[20px] font-bold text-gray-900 mb-2">
            {wasTrial ? t('subscription_expired.trial_expired_heading') : t('subscription_expired.subscription_expired_heading')}
          </h2>
          {formattedExpireDate && (
            <div className="flex items-center justify-center gap-1.5 mb-2 text-gray-500">
              <Clock className="h-4 w-4 shrink-0" />
              <span className="text-[13px]">
                {t('subscription_expired.expired_on_date', { date: formattedExpireDate })}
              </span>
            </div>
          )}
          <p className="text-[13px] text-gray-500">
            {t('subscription_expired.subscription_required_access')}
          </p>
        </div>

        {/* Limited access notice */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
          <p className="text-[15px] font-bold text-gray-900 mb-2">{t('subscription_expired.limited_access_mode')}</p>
          <p className="text-[13px] text-gray-500 mb-4">
            {wasTrial
              ? t('subscription_expired.trial_expired_description')
              : t('subscription_expired.subscription_expired_description')
            }
          </p>
          <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 flex items-center gap-3">
            <Crown className="text-brand-600 h-5 w-5 shrink-0" />
            <span className="text-[13px] text-brand-700 font-medium">{t('subscription_expired.premium_access_subscribe')}</span>
          </div>
        </div>

        {/* Subscription Button */}
        {isMobilePlatform ? (
          <Button
            className="w-full h-12 text-[15px] font-semibold bg-brand-600 hover:bg-brand-700 text-white rounded-xl"
            onClick={() => navigate("/subscription", { state: { returnPath } })}
          >
            <Crown className="mr-2 h-5 w-5" />
            {t('subscription_expired.view_subscription_plans')}
          </Button>
        ) : (
          <Button
            variant="outline"
            className="w-full h-12 text-[15px] font-semibold border-brand-300 text-brand-700 hover:bg-brand-50 rounded-xl"
            onClick={() => navigate("/subscription", { state: { returnPath } })}
          >
            <Crown className="mr-2 h-5 w-5" />
            {t('subscription_expired.view_subscription_plans')}
          </Button>
        )}

        <p className="text-[11px] text-center text-gray-400 pb-2">
          {t('subscription_expired.payment_info')}
        </p>
      </main>

      <BottomNavigation />
    </div>
  );
};

export default SubscriptionExpiredPage;
